import {
  Box,
  Button,
  CardActions,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import React from "react";
import type { ServicesLabel } from "../../services";
import { useNavigate, useSearchParams } from "react-router-dom";
import ArrowRight from "../../../../assets/arrow_right.svg";
import {
  CloseRounded,
  DeleteOutlineRounded,
  DoneRounded,
} from "@mui/icons-material";
import { ServiceList } from "../../model/service_model";
import useUser from "../../../../providers/user/user_provider";
import { useServicesFilterStore } from "../../../../store/use-services-filter-store";
import { black, greenLight, purple, red, redLight } from "../../../../theme/colors";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

interface DataGridInterface {
  labels: ServicesLabel[];
  data: ServiceList[];
  count: number;
  page: number;
  setPage: (val: number) => void;
  setSelectedUserId: (val: ServiceList) => void;
  approvingId: number | null;
  setApproveServiceDialog: (val: boolean) => void;
  setCloseRejectServiceStatusDialog: (val: boolean) => void;
  setCloseDeleteServiceStatusDialog: (val: boolean) => void;
}

export default function DatGridServices({
  labels,
  data,
  page,
  setPage,
  count,
  setSelectedUserId,
  approvingId,
  setApproveServiceDialog,
  setCloseRejectServiceStatusDialog,
  setCloseDeleteServiceStatusDialog,
}: DataGridInterface) {
  const navigate = useNavigate();
  const { hasPermission, userPermissions } = useUser();
  const { pageSize, setField } = useServicesFilterStore();
  const [, setSearchParams] = useSearchParams();

  const startRange = page * pageSize + 1;
  const endRange = Math.min((page + 1) * pageSize, count);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
    setSearchParams({ page: (newPage + 1).toString() });
    setField("page", newPage + 1);
    setField("applyFilter", true);
  };

  const handleChangePageSize = (newPageSize: number) => {
    setField("pageSize", newPageSize);
    setField("page", 1);
    setSearchParams({ page: "1" });
  };

  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;

  function statusColorCodeBasedOnStatus({ status }: { status: string }) {
    if (
      status === "Modified" ||
      status === "Pending" ||
      status === "In Progress"
    ) {
      return purple;
    } else if (status === "Approved") {
      return "#1E6446";
    } else {
      return red;
    }
  }

  function actionComponent({
    status,
    actionOneDisable,
    actionTwoDisable,
    selectedItem,
  }: {
    status: string;
    actionOneDisable?: boolean;
    actionTwoDisable?: boolean;
    selectedItem: ServiceList;
  }) {
    const isApproving = selectedItem.id === approvingId;

    switch (status) {
      case "Pending":
      case "Modified":
      case "Submitted":
      case "In Progress":
        return hasPermission("services", "update") ? (
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            <Button
              size="small"
              disabled={isApproving}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUserId(selectedItem);
                setApproveServiceDialog(true);
              }}
              startIcon={
                isApproving ? (
                  <CircularProgress size={14} sx={{ color: "#1E6446" }} />
                ) : (
                  <DoneRounded fontSize="small" />
                )
              }
              sx={{
                bgcolor: greenLight,
                borderRadius: 3,
                color: "#1E6446",
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "#D6EEE0" },
              }}
            >
              Approve
            </Button>

            <Button
              size="small"
              disabled={isApproving}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUserId(selectedItem);
                setCloseRejectServiceStatusDialog(true);
              }}
              startIcon={<CloseRounded fontSize="small" />}
              sx={{
                bgcolor: redLight,
                borderRadius: 3,
                ml: 1.5,
                color: red,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "#FBD9D6" },
              }}
            >
              Reject
            </Button>
          </Box>
        ) : null;
      case "Rejected":
      case "Approved":
        return (
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            {hasPermission("services", "update") && (
              <Button
                size="small"
                disabled
                startIcon={<DoneRounded fontSize="small" />}
                sx={{
                  bgcolor: greenLight,
                  borderRadius: 3,
                  color: black,
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  opacity: actionOneDisable ? 0.4 : 1,
                }}
              >
                {status === "Approved" ? "Approved" : "Rejected"}
              </Button>
            )}

            {hasPermission("services", "delete") && (
              <Button
                size="small"
                disabled={isApproving || actionTwoDisable}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedUserId(selectedItem);
                  setCloseDeleteServiceStatusDialog(true);
                }}
                startIcon={<DeleteOutlineRounded fontSize="small" />}
                sx={{
                  bgcolor: redLight,
                  borderRadius: 3,
                  ml: 1.5,
                  color: red,
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  opacity: actionTwoDisable ? 0.4 : 1,
                  "&:hover": { bgcolor: "#FBD9D6" },
                }}
              >
                Delete
              </Button>
            )}
          </Box>
        );
      default:
        return null;
    }
  }

  function gridCell({ label, text, flex }: { label?: string; text: string; flex?: number }) {
    return (
      <TableCell label={label} flex={flex} bordered>
        <Typography fontSize={14} color={black} noWrap>
          {text ? text : "-"}
        </Typography>
      </TableCell>
    );
  }

  const showEmpty = data?.length === 0;

  return (
    <Box display="flex" flex={1} flexDirection="column" width="100%">
      <CardContent sx={{ p: { xs: 2.5, sm: 3 }, display: "flex", flex: 1, flexDirection: "column" }}>
        {showEmpty ? (
          <TableEmptyState
            message="No services found"
            hint="Try adjusting your search or filters."
          />
        ) : (
          <Box mt={1}>
            <TableHeaderRow labels={labels} lastColumnWidth={280} bordered />
            {data.map((item) => {
              const statusColorCode = statusColorCodeBasedOnStatus({
                status: item?.status,
              });

              return (
                <TableRow
                  key={item.id}
                  onClick={() => navigate(`/services/serviceDetails/${item.id}`)}
                  bordered
                >
                  {gridCell({ label: "Sl. No.", text: item?.id?.toString(), flex: 0.5 })}
                  {gridCell({ label: "Name of Service / Person", text: item?.name, flex: 1.8 })}
                  {gridCell({ label: "Surveyor", text: item?.surveyorName, flex: 1 })}
                  {gridCell({ label: "Service Category", text: item?.category, flex: 1.5 })}
                  {gridCell({ label: "State", text: item?.state, flex: 1 })}
                  {gridCell({ label: "Contact Number", text: `+91 ${item?.mobileNumber}`, flex: 1 })}
                  <TableCell label="Status" flex={0.7} bordered>
                    <Typography fontSize={14} fontWeight={600} color={statusColorCode} noWrap>
                      {item?.status}
                    </Typography>
                  </TableCell>
                  <TableCell isLast lastColumnWidth={280} bordered>
                    <Box
                      display="flex"
                      alignItems="center"
                      flexWrap="wrap"
                      justifyContent="flex-start"
                      gap={1}
                    >
                      {actionComponent({
                        status: item.status,
                        actionOneDisable:
                          item.status === "Approved" || item.status === "Rejected",
                        actionTwoDisable: item.status === "Rejected",
                        selectedItem: item,
                      })}
                      <Box display="flex" sx={{ display: { xs: "none", sm: "flex" } }}>
                        <img src={ArrowRight} alt="" height={15} width={15} />
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </Box>
        )}
      </CardContent>

      {!showEmpty && (
        <CardActions sx={{ px: { xs: 2.5, sm: 3 }, pb: 3, flexDirection: "column", alignItems: "flex-start" }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={page}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={handleChangePageSize}
            />
          </Box>
          {userPermissions?.superAdmin && (
            <Button
              onClick={() => navigate("/reports")}
              variant="text"
              sx={{
                mt: 1,
                px: 0,
                color: purple,
                textTransform: "none",
                fontWeight: 600,
                textDecoration: "underline",
                "&:hover": { textDecoration: "underline", bgcolor: "transparent" },
              }}
            >
              Back To Report
            </Button>
          )}
        </CardActions>
      )}
    </Box>
  );
}
